import {
  CheckOutlined,
  CloseOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  LoadingOutlined,
  LockFilled,
  LoginOutlined,
  ReloadOutlined,
  UnlockFilled,
} from '@ant-design/icons';
import { Alert, App, Button, Card, Checkbox, Col, Divider, Form, Input, Row, Spin, Tag, theme, Tooltip, Typography } from 'antd';
import { RuleObject } from 'antd/es/form';
import moment from 'moment';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectCountry from 'src/common/components/business/country-select';
import LanguageSelect from 'src/common/components/business/language-select';
import { getDateFormat } from 'src/common/utils';
import { useIdentity } from 'src/core/services/authentication';
import { formatMessage } from 'src/core/services/http.service';
import { nameof } from 'src/core/utils/object';
import { AppConfig, container } from 'src/inversify.config';
import {
  getDefaultOrganizationTestingEndDate,
  OrganizationCreationStatus,
  OrganizationImportStore,
  OrganizationItem,
  OrganizationItemStore,
} from 'src/stores/organizations-store';
import { curateString } from 'src/core/utils/object';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { OrganizationExportImportContext, OrganizationExportImportContextAction, OrganizationExportState } from '../organization-export-context';
import { ENV } from 'src';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { PermissionsConstants } from 'src/common/types';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';

const { Text } = Typography;

const loadingIcon = <LoadingOutlined style={{ fontSize: 24, color: 'gray' }} spin />;

const langs: { [key: string]: string } = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  ru: 'Russian',
  pt: 'Portuguese',
};

const { useToken } = theme;

const permissions = [PermissionsConstants.ManagePartnerOrganization];

const OrganizationGeneralPropertyEditCard: FC<{
  organizationId: string;
  canEditOrg: boolean;
  canBlockOrg: boolean;
  canCreateOrg: boolean;
  canDeleteOrg: boolean;
  organizationDisplayName: string;
}> = ({ organizationId, canEditOrg, canBlockOrg, canCreateOrg, organizationDisplayName, canDeleteOrg }) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const [form] = Form.useForm();
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [error, setError] = useState<string | undefined>('');
  const organizationImportStore = container.get(OrganizationImportStore);

  const { organizationExportState, organizationExportDispatch } = useContext(OrganizationExportImportContext);
  const [isExporting, setIsExporting] = useState(organizationExportState.exportState.get(organizationId)?.state === OrganizationExportState.Busy);
  const { modal, notification } = App.useApp();
  const [parentOrgData, setParentOrgData] = useState<OrganizationItem>();
  const loadingParentData = useRef(false);
  const { token } = useToken();
  const [isTestOrg, setIsTestOrg] = useState<boolean>(false);
  const firstLoad = useRef(false);
  const preventRefresh = useRef<boolean>(false);

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const canManagePartnerOrg = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization);
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);

  const { TextArea } = Input;

  useEffect(() => {
    if (preventRefresh.current) {
      preventRefresh.current = false;
      return;
    }
    const loadParentData = async () => {
      if (
        organizationState?.item?.value?.id === organizationId &&
        organizationState?.item?.value?.parentOrganizationId &&
        !loadingParentData.current
      ) {
        loadingParentData.current = true;
        const data = await organizationStore.getOrganizationData(organizationState.item.value.parentOrganizationId);
        setParentOrgData(data);
      }
    };

    loadParentData();
    if (organizationState?.item?.value?.id === organizationId && !firstLoad.current) {
      setIsTestOrg(organizationState.item.value.isTestingOrg);
      setMfaEnabled(organizationState?.item?.value?.twoFactorEnabled ?? false);
      firstLoad.current = true;
    }
    form.resetFields();
  }, [organizationState.item.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    if (!canEditOrg) return;

    setError('');
    let item = await form.validateFields();
    item.displayName = curateString(item.displayName);
    item.address = curateString(item.address);
    item.isTestingOrg = isTestOrg;
    item.testingEndDate = isTestOrg ? item.testingEndDate : null;
    const response = await organizationStore.save(organizationId, item as OrganizationItem);
    if (response?.isSuccess) {
      organizationState.set((s) => {
        s.item.displayName = curateString(item.displayName);
        s.item.email = item.email;
        s.item.address = curateString(item.address);
        s.item.defaultLanguage = item.defaultLanguage;
        s.item.country = item.country;
        s.item.isTestingOrg = isTestOrg;
        s.item.testingEndDate = isTestOrg ? item.testingEndDate : undefined;
        return s;
      });
      form.resetFields();
    } else setError(formatMessage(response));
  };

  const onReset = () => {
    setError('');
    setIsTestOrg(organizationState.item.value.isTestingOrg);
    form.resetFields();
  };

  const canEnsureDb = organizationState.item.value?.creationStatus === OrganizationCreationStatus.Error && canCreateOrg;

  const onEnqueueJob = async () => {
    if (!canEnsureDb) return;

    const response = await organizationStore.restartOrganizationCreationFlow(organizationId);
    if (response?.isSuccess) {
      modal.success({
        title: t('CreationJobForOrganizationEnqueued'),
        okText: t('OK'),
      });
    } else {
      modal.error({
        title: t('SomethingWentWrong'),
        content: formatMessage(response),
        okText: t('OK'),
      });
    }
  };

  const onChangeBlockStatus = async () => {
    if (!canBlockOrg) return;

    const nextValue = !organizationState.item.value?.isBlocked;
    const response = await organizationStore.changeOrganizationBlockStatus(organizationId, nextValue);
    if (response && response.isSuccess) {
      organizationState.set((s) => {
        s.item.isBlocked = nextValue;
        return s;
      });
      modal.success({
        title: nextValue ? t('Organization has been blocked') : t('OrganizationHasBeenUnblocked'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    } else {
      modal.error({
        title: t('SomethingWentWrong'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    }
  };

  let isPartnerValue = organizationState.item.value?.isPartner ? t('Yes') : t('No');
  let isBlockedValue = organizationState.item.value?.isBlocked ? t('Yes') : t('No');

  const onExport = async () => {
    const organizationIsLocked = await organizationStore.checkLockedStatus(organizationId);

    if (organizationIsLocked) {
      notification.error({
        placement: 'bottomRight',
        duration: 20,
        style: {
          width: 600,
        },
        message: t('OrganizationIsLocked'),
      });
      return;
    }

    const exportingPromise = organizationImportStore.exportFile(
      organizationState.item.id.value,
      `${organizationState.item.displayName.value}_backup.db`
    );

    setIsExporting(true);

    organizationExportDispatch({
      type: OrganizationExportImportContextAction.UpdateExportState,
      organizationId: organizationId,
      state: OrganizationExportState.Busy,
      promise: exportingPromise,
    });

    const result = await exportingPromise;

    if (result && result.isSuccess) {
      notification.success({
        message: t('ExportingOrganizationSuccess'),
        placement: 'bottomRight',
      });
    } else {
      notification.error({
        message: (
          <>
            <span>{t('ErrorExportingOrganization').replace('{0}', organizationDisplayName)}</span>
            {config.environment === ENV.DEV && (
              <>
                <br />
                <br />
                <span>{`${result?.error}`}</span>
              </>
            )}
          </>
        ),
        placement: 'bottomRight',
      });
    }

    organizationExportDispatch({
      type: OrganizationExportImportContextAction.UpdateExportState,
      state: OrganizationExportState.Available,
      organizationId: organizationId,
      promise: exportingPromise,
    });
    setIsExporting(false);
  };
  return (
    <Card
      title={<h3 style={{ textAlign: 'left', marginLeft: 16, textWrap: 'wrap' }}>{t('GeneralProperties')}</h3>}
      extra={
        organizationState?.item?.value?.creationStatus != null ? (
          <>
            <Tooltip title={organizationState.item.value.creationStatusDescription}>
              <Tag>{organizationState.item.value.creationStatus}</Tag>
            </Tooltip>
            {organizationState?.item?.value?.creationStatus != null &&
              organizationState?.item?.value?.creationStatus !== OrganizationCreationStatus.Error &&
              organizationState?.item?.value?.creationStatus !== OrganizationCreationStatus.Available && (
                <ReloadOutlined onClick={() => organizationStore.load(organizationId)} />
              )}
          </>
        ) : null
      }
    >
      {(error || '').length !== 0 && <Alert type="error" message={t(error || '')} />}
      <Form form={form} layout="vertical" autoComplete="off" style={{ margin: '16px 40px 40px 40px' }}>
        {canEditOrg ? (
          <>
            <Form.Item
              initialValue={organizationState.item.value?.displayName}
              name={nameof<OrganizationItem>('displayName')}
              label={t('DisplayName')}
              rules={[
                { required: true, message: t('PleaseEnterName') },
                () => ({
                  validator: (ruleObj: RuleObject, value: string) => {
                    const maxLenght = 128;
                    if (value === undefined) return Promise.resolve();
                    if (value.length > maxLenght) {
                      return Promise.reject(t('TextFieldLimitError').replace('{0}', t('DisplayName')).replace('{1}', `${maxLenght}`));
                    } else return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input autoComplete="off" allowClear placeholder={t('DisplayName')} />
            </Form.Item>
            <Form.Item
              name={nameof<OrganizationItem>('email')}
              initialValue={organizationState.item.value?.email}
              label={t('Email')}
              rules={[
                { required: true, type: 'email', message: t('PleaseEnterAValidEmail') },
                () => ({
                  validator: (ruleObj: RuleObject, value: string) => {
                    const maxLenght = 256;
                    if (value === undefined) return Promise.resolve();
                    if (value.length > maxLenght) {
                      return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Email')).replace('{1}', `${maxLenght}`));
                    } else return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input autoComplete="off" allowClear placeholder={t('Email')} />
            </Form.Item>
            <Form.Item
              name={nameof<OrganizationItem>('address')}
              initialValue={organizationState.item.value?.address}
              label={t('Address')}
              rules={[{ required: true, message: t('PleaseEnterAnAddress') }]}
            >
              <TextArea rows={3} autoComplete="off" allowClear placeholder={t('Address')} />
            </Form.Item>
            {canManagePartnerOrg ? (
              <Form.Item
                initialValue={organizationState.item.value?.country}
                label={t('Country')}
                name={nameof<OrganizationItem>('country')}
                rules={[{ required: true, message: t('PleaseSelectCountry') }]}
              >
                <SelectCountry />
              </Form.Item>
            ) : (
              <Form.Item>
                <span>
                  {t('Country')}: {organizationState.item.value?.country}
                </span>
              </Form.Item>
            )}
            <Form.Item
              name="defaultLanguage"
              initialValue={organizationState.item.value?.defaultLanguage}
              label={t('DefaultLanguage')}
              rules={[{ required: true, message: t('Please select a language') }]}
            >
              <LanguageSelect />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item label={t('DisplayName')}>
              <span>{organizationState.item.value?.displayName}</span>
            </Form.Item>
            <Form.Item label={t('Email')}>
              <span>{organizationState.item.value?.email}</span>
            </Form.Item>
            <Form.Item label={t('Address')}>
              <span>{organizationState.item.value?.address}</span>
            </Form.Item>
            <Form.Item>
              <span>
                {t('Country')}: {organizationState.item.value?.country}
              </span>
            </Form.Item>
            <Form.Item label={t('DefaultLanguage')}>
              <span>{organizationState.item.value?.defaultLanguage && t(langs[organizationState.item.value.defaultLanguage as string])}</span>
            </Form.Item>
          </>
        )}
        <Row gutter={[16, 8]}>
          <Col span={8}>
            <Form.Item style={{ width: '100%' }} label={t('IsPartner')}>
              <Input value={isPartnerValue} readOnly autoComplete="off" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item style={{ width: '100%' }} label={t('IsBlocked')}>
              <Input value={isBlockedValue} readOnly autoComplete="off" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={t('PartnerOrganization')}>
          <span>
            {organizationState.item.value?.parentOrganizationDisplayName ? (
              <Tag> {organizationState.item.value?.parentOrganizationDisplayName}</Tag>
            ) : (
              '-'
            )}
          </span>
        </Form.Item>
        <>
          {!organizationState.item.value?.isPartner && !canDeleteOrg && (
            <>
              <Form.Item name={'testingEndDate'} hidden initialValue={moment(organizationState.item.value?.testingEndDate)} />
            </>
          )}
          {organizationState.item.value?.isTestingOrg && (organizationState.item.value?.isPartner || !canDeleteOrg) && (
            <Row style={{ marginBottom: 10 }}>
              <Col span={24} style={{ color: token.colorError }}>{`${t('TestOrganization')}`}</Col>
              <Col span={24}>
                <span>{`${t('RemovalDate')}: ${moment.utc(organizationState.item.value?.testingEndDate).local().format(getDateFormat())}`}</span>
                <Form.Item name={'testingEndDate'} hidden initialValue={moment(organizationState.item.value?.testingEndDate)} />
              </Col>
            </Row>
          )}
          {canDeleteOrg && !organizationState.item.value?.isPartner && parentOrgData?.isTestingOrg && (
            <>
              <Col span={24} style={{ color: token.colorError }}>{`${t('TestOrganization')}`}</Col>
              <Col sm={13} xs={24} style={{ marginTop: 10, paddingRight: 5 }}>
                <span>{t('RemovalDate')}</span>
              </Col>
              <Col sm={11} xs={24} style={{ marginBottom: 20 }}>
                <Form.Item
                  initialValue={
                    organizationState.item.value?.testingEndDate
                      ? moment(organizationState.item.value?.testingEndDate)
                      : getDefaultOrganizationTestingEndDate()
                  }
                  name={nameof<OrganizationItem>('testingEndDate')}
                  rules={[() => ({ required: true })]}
                  noStyle
                >
                  <DatePicker allowClear={false} style={{ width: '100%' }} format="DD/MM/YYYY" maxDate={moment(parentOrgData.testingEndDate)} />
                </Form.Item>
              </Col>
            </>
          )}
          {canDeleteOrg && !organizationState.item.value?.isPartner && !parentOrgData?.isTestingOrg && (
            <>
              <Col span={24}>
                <Form.Item valuePropName="checked">
                  <Checkbox
                    checked={isTestOrg}
                    onChange={(e) => {
                      preventRefresh.current = true;
                      setIsTestOrg(() => e.target.checked);
                      form.setFieldValue('isTestingOrg', e.target.checked);
                    }}
                  >
                    {t('IsTestOrganization')}
                  </Checkbox>
                </Form.Item>
              </Col>
              {!isTestOrg && <Form.Item name={'testingEndDate'} hidden initialValue={moment(organizationState.item.value?.testingEndDate)} />}
              {isTestOrg && (
                <>
                  <Col sm={13} xs={24} style={{ marginTop: 10, paddingRight: 5 }}>
                    <span>{t('RemovalDate')}</span>
                  </Col>
                  <Col sm={11} xs={24} style={{ marginBottom: 20 }}>
                    <Form.Item
                      initialValue={
                        organizationState.item.value?.testingEndDate
                          ? moment(organizationState.item.value?.testingEndDate)
                          : getDefaultOrganizationTestingEndDate()
                      }
                      name={nameof<OrganizationItem>('testingEndDate')}
                      rules={[() => ({ required: true })]}
                      noStyle
                    >
                      <DatePicker allowClear={false} style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                </>
              )}
            </>
          )}
        </>
        {(canEditOrg || canBlockOrg || canEnsureDb) && (
          <Form.Item style={{ marginBottom: 0 }}>
            {canEditOrg && (
              <>
                <Row gutter={[16, 8]}>
                  <Col lg={12} xs={24} style={{ minWidth: 80 }}>
                    <Button htmlType="button" type="default" disabled={organizationState.isBusy.value} onClick={onReset} style={{ width: '100%' }}>
                      {t('Reset')}
                    </Button>
                  </Col>
                  <Col lg={12} xs={24} style={{ minWidth: 130 }}>
                    <Button
                      type="primary"
                      disabled={organizationState.isBusy.value}
                      onClick={() => {
                        if (isTestOrg && !organizationState.item.value.isTestingOrg) {
                          modal.confirm({
                            title: t('TestOrgConfirmationMessage'),
                            onOk: () => onSave(),
                          });
                        } else onSave();
                      }}
                      style={{ width: '100%' }}
                    >
                      {t('SaveChanges')}
                    </Button>
                  </Col>
                </Row>
                <Divider />
              </>
            )}
            <Row gutter={[16, 8]}>
              {canBlockOrg && (
                <Col lg={12} xs={24} style={{ minWidth: 140 }}>
                  <Button
                    type="default"
                    danger={!organizationState.item.value?.isBlocked}
                    disabled={organizationState.isBusy.value}
                    onClick={onChangeBlockStatus}
                    icon={!organizationState.item.value?.isBlocked ? <LockFilled /> : <UnlockFilled />}
                    style={{ width: '100%' }}
                  >
                    {organizationState.item.value?.isBlocked ? t('Unblock') : t('Block')}
                  </Button>
                </Col>
              )}
              {canEnsureDb && (
                <Col lg={12} xs={24} style={{ minWidth: 140 }}>
                  <Button
                    type="primary"
                    disabled={organizationState.isBusy.value}
                    onClick={onEnqueueJob}
                    icon={<DatabaseOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t('RestartCreation')}
                  </Button>
                </Col>
              )}
              {canManagePartnerOrg && (
                <>
                  <Divider />
                  <div style={{ marginBottom: 4 }}>
                    <Text strong>{t('MFAStatus')}: </Text>
                    {mfaEnabled && (
                      <>
                        <CheckOutlined style={{ color: token.colorSuccess, marginLeft: 4, marginRight: 4 }} />
                        <Text style={{ color: token.colorSuccess }}>{t('Enabled')}</Text>
                      </>
                    )}
                    {!mfaEnabled && (
                      <>
                        <CloseOutlined style={{ color: token.colorError, marginLeft: 4, marginRight: 4 }} />
                        <Text style={{ color: token.colorError }}>{t('Disabled')}</Text>
                      </>
                    )}
                  </div>
                  <Col span={24}>
                    <Button
                      type="default"
                      disabled={organizationState.isBusy.value}
                      onClick={async () => {
                        const result = await organizationStore.switchMFA(organizationId, !mfaEnabled);
                        if (result?.isSuccess) {
                          organizationState.set((s) => {
                            s.item.twoFactorEnabled = !mfaEnabled;
                            return s;
                          });
                          setMfaEnabled((prev) => !prev);
                        }
                      }}
                      icon={<LoginOutlined />}
                      style={{ width: '100%', height: 'auto', minHeight: '60px', textWrap: 'wrap' }}
                    >
                      {mfaEnabled ? t('DisableMFA') : t('EnableMFA')}
                    </Button>
                  </Col>
                </>
              )}
              {canManagePartnerOrg && (
                <Col span={24} style={{ minWidth: 140 }}>
                  <Button
                    className={isExporting ? 'export-organization-button' : ''}
                    type="default"
                    htmlType="button"
                    disabled={organizationState.isBusy.value || isExporting}
                    onClick={onExport}
                    style={{ width: '100%', minHeight: 56, height: 'fit-content' }}
                  >
                    {!isExporting && <DownloadOutlined />}
                    {isExporting && (
                      <Spin
                        spinning={isExporting}
                        indicator={loadingIcon}
                        size="small"
                        style={{ paddingLeft: '10px' }}
                        className="exporting-spinner"
                      />
                    )}
                    <span style={{ padding: '30' }}>{isExporting ? t('OrganizationIsExporting') : t('ExportOrganization')}</span>
                  </Button>
                </Col>
              )}
            </Row>
          </Form.Item>
        )}
      </Form>
    </Card>
  );
};

export default OrganizationGeneralPropertyEditCard;
